import { useState } from 'react';

export interface FocalPoint {
  x: number; // 0..1 (0 = left, 1 = right)
  y: number; // 0..1 (0 = top, 1 = bottom)
}

interface SmartImageProps {
  src?: string | null;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  sizes?: string;
  aspectClassName?: string;
  /** Focal point (0..1) used as object-position when image is cropped */
  focalPoint?: FocalPoint | null;
  /** object-fit override (defaults to cover) */
  fit?: 'cover' | 'contain';
}

/**
 * Modern responsive image component.
 * - Forces WebP via Supabase Storage render endpoint (auto content-negotiation).
 * - Dense srcset (400 → 2400) for sharp Retina rendering on every device.
 * - Low-quality blurred placeholder (LQIP) while the full image loads.
 * - Honors a `focalPoint` so cropped images keep the subject centered.
 */
export const SmartImage = ({
  src,
  alt,
  className = '',
  loading = 'lazy',
  fetchPriority,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  aspectClassName,
  focalPoint,
  fit = 'cover',
}: SmartImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  /**
   * Build a Supabase render URL that triggers on-the-fly image transformation.
   * Switches `/object/public/` → `/render/image/public/` so Supabase actually
   * resizes + serves WebP based on the client Accept header.
   */
  const toRenderBase = (url: string): string | null => {
    if (!url.includes('supabase.co/storage/v1/object/public/')) return null;
    // Strip any existing query string (e.g. ?t=cache-buster) · we'll add our own.
    const clean = url.split('?')[0];
    return clean.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
  };

  const buildResponsive = (url: string) => {
    // Supabase image transformation API is not enabled on this project's plan
    // (returns 403). Serve the original asset directly · buckets already store
    // optimized files. We skip the LQIP to avoid an extra failed request.
    return {
      src: url,
      srcSet: undefined as string | undefined,
      lqip: undefined as string | undefined,
    };
  };

  if (!src || errored) {
    return (
      <div className={`bg-muted ${aspectClassName ?? ''} ${className}`} aria-label={alt} />
    );
  }

  const { src: finalSrc, srcSet, lqip } = buildResponsive(src);

  const fp: FocalPoint = focalPoint ?? { x: 0.5, y: 0.5 };
  const objectPosition = `${Math.round(fp.x * 100)}% ${Math.round(fp.y * 100)}%`;
  const fitClass = fit === 'contain' ? 'object-contain' : 'object-cover';

  // If caller passes an absolute-positioning wrapper class, don't add `relative`
  // (Tailwind compiles `relative` after `absolute` so it would win and break layout).
  const hasAbsolute = (aspectClassName ?? '').includes('absolute');
  const positionClass = hasAbsolute ? '' : 'relative';

  return (
    <div className={`${positionClass} overflow-hidden ${aspectClassName ?? ''}`}>
      {/* Blurred low-quality placeholder */}
      {lqip ? (
        <img
          src={lqip}
          alt=""
          aria-hidden
          className={`absolute inset-0 w-full h-full ${fitClass} scale-110 blur-xl transition-opacity duration-700 ${
            loaded ? 'opacity-0' : 'opacity-100'
          }`}
          style={{ objectPosition }}
        />
      ) : (
        <div
          className={`absolute inset-0 bg-muted transition-opacity duration-700 ${loaded ? 'opacity-0' : 'opacity-100'}`}
          aria-hidden
        />
      )}
      <img
        src={finalSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        style={{ objectPosition }}
        className={`relative w-full h-full ${fitClass} transition-opacity duration-700 ease-out ${
          loaded ? 'opacity-100' : 'opacity-0'
        } ${className}`}
      />
    </div>
  );
};

export default SmartImage;